import { FocusEvent } from 'react';
import { defaultTooltipToggleId } from '../../components/Form/idHelpers';
import { keys } from '../typedObjectMethods';
import buildNestedFieldId from './buildNestedFieldId';
import { FieldErrors, ValidationRule, ValidationRules } from './types';

/**
 * Helper method to find all the field IDs associated with a data object.
 */
export const getFieldIds = <TData extends { [s: string]: unknown } | ArrayLike<unknown>>(
  data: TData
): (string | number)[] =>
  Object.entries(data).flatMap(([id, valueOrNestedValues]) =>
    Array.isArray(valueOrNestedValues)
      ? valueOrNestedValues.flatMap((nestedObject, index) =>
          getFieldIds(nestedObject).map((nestedId) =>
            buildNestedFieldId(id, [nestedId, index])
          )
        )
      : id
  );

const evaluateNestedErrors = <TData, TRoot>(
  data: TRoot,
  nestedData: TData,
  rules: ValidationRules<TData, TRoot>,
  submitFailed: boolean,
  ...indices: number[]
): FieldErrors => {
  const errors: FieldErrors = {};

  keys(rules).forEach((id) => {
    const valueOrNestedValues = nestedData[id];

    if (!Array.isArray(valueOrNestedValues)) {
      // Simple field - an array of rules we can evaluate
      const fieldRules = rules[id] as ValidationRule<unknown, TRoot>[] | undefined;

      const error = fieldRules?.find(
        (rule) =>
          (!rule.onlyValidateAfterSubmission || submitFailed) &&
          !rule.validityCondition(valueOrNestedValues, data, ...indices)
      )?.errorMessage;

      if (error) {
        errors[id as string] = error;
      }
    } else if (rules[id] !== undefined) {
      // Nested field - recursively apply to the nested values and corresponding rules
      const fieldRules = rules[id] as ValidationRules<TData[keyof TData], TRoot>;

      valueOrNestedValues.forEach((nestedObject, index) => {
        const nestedErrors = evaluateNestedErrors(
          data,
          nestedObject,
          fieldRules,
          submitFailed,
          ...indices,
          index
        );

        Object.entries(nestedErrors).forEach(([nestedId, error]) => {
          errors[buildNestedFieldId(id as string, [nestedId, index])] = error;
        });
      });
    }
  });

  return errors;
};

/**
 * Helper method to generate the error object from the data and its rules.
 *
 * @param data the data object
 * @param rules the validation rules for the data object
 */
export const evaluateErrors = <TData>(
  data: TData,
  rules: ValidationRules<TData>,
  submitAttempted: boolean
): FieldErrors => {
  return evaluateNestedErrors(data, data, rules, submitAttempted);
};

/**
 * If a user blurs a field to show the help text, we don't want to show error validation while the user is trying to complete the field.
 * This helper allows us to identify when a user has clicked from a field to the field's 'show help' toggle.
 * We use this in the 'onBlur' behavior of a question field to conditionally not showValidation when the user toggles the help text.
 */
export const eventTargetsHelpText = (event: FocusEvent, fieldId: string): boolean => {
  return (<HTMLElement>event.relatedTarget)?.id === defaultTooltipToggleId(fieldId);
};
